import { Bound } from "lib/@components/binding/Bound"
import { CompareButtonsBelow, DifferenceEntry, DifferenceHeader } from "slot-definitions"
import { inTransition, noChange, useRefresh } from "lib/@hooks/useRefresh"
import { MapPlus } from "library/map-plus/index.mjs"
import { Box, Card, CardHeader, Divider, Stack, Typography } from "@mui/material"
import { AcceptedChanged, IsAccepted } from "event-definitions"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { Frag } from "lib/@components/slot/frag"
import { NoResults } from "lib/@components/no-results/no-results"
import { InlineCompareButtons } from "./inline-approval-buttons/inline-compare-buttons"
import { getAuditsByReferenceIdAndType } from "routes/audit/controller/get-audits-by-reference-id"
import { ReasonComponentComparison } from "../edit/reason-component-comparison"
import { ScheduleReviewJustification } from "./schedule-review-justification"
import { getAttributesFor } from "./register-task-comparitor"
import { defaultNewHeader, defaultNewTask } from "../lib/default-tasks"
import { createSlot } from "lib/@components/slot/create-slot"

export const ComparisonTypeContentWrapper = createSlot("ComparisonTypeContentWrapper")

function groupByToMap(list, fn = (v) => v) {
    const output = new MapPlus(() => [])
    list.forEach((item) => {
        const group = fn(item)
        output.get(group).push(item)
    })
    return output
}

const DELETE = "DELETE"

export function getIdForComponent(component) {
    const label =
        component.props?.children?.props?.label ?? component.props?.content?.props?.label ?? component.props?.label
    return (
        component?.props.task?.id ??
        (label === "Introduction" ? "intro" : label === "Details" ? "details" : "movements")
    )
}

const idFromGroup = {
    Introduction: "intro",
    "Task Order": "movements",
    Details: "details",
}

export function ComparisonType({ type, list }) {
    CompareButtonsBelow.usePlug(<ScheduleReviewJustification />)
    const { target } = useBoundContext()
    const typeSlot = DifferenceHeader(type)
    const refresh = useRefresh(inTransition)
    const groups = groupByToMap(
        list,
        (c) =>
            c?.props?.children?.props?.children?.props?.label ??
            c?.props?.children?.props?.label ??
            c?.props?.label ??
            DELETE
    )
    groups.delete(DELETE)
    const referenceId = `${target?.id}-version:${parseInt(target?.version) + 1}`
    const draftData = getAuditsByReferenceIdAndType.useResults(referenceId, "customSchedule", {
        shortDescription: "New Draft Created",
    })
    const draftId = draftData?.[0]?.id
    const groupReviewId = target?.groupReviewId
    const taskAudits =
        getAuditsByReferenceIdAndType.useResults(referenceId, "customSchedule", {
            "payload.changeType": "task",
        }) ?? []

    return (
        <Bound refresh={refresh} draftId={draftId} groupReviewId={groupReviewId} taskAudits={taskAudits}>
            <typeSlot.Slot type={type} list={list} />
            {!groups.size && (
                <NoResults>
                    <Box sx={{ textAlign: "center" }} data-cy="no-significant-differences">
                        No significant differences
                        <Typography sx={{ opacity: 0.7 }} variant="caption" component="div" color="text.secondary">
                            Differences in case, spacing and punctuation alone are not displayed.
                        </Typography>
                    </Box>
                </NoResults>
            )}
            {Array.from(groups.entries()).map(([group, list]) => {
                let header
                const toRender = list.map((l, index) => {
                    header ??= <l.type key={l.key ?? index} {...l.props} children={null} />
                    return l.props.children
                })
                const id = idFromGroup[group] ?? group
                const reviewIds = getReviewIdsForGroup({ id, list })
                const isTaskOrder = id === "movements"
                const { to, from } = getCompareTargets(list)
                const isIntro = id === "intro"
                const isTask = id && id !== "details" && id !== "intro"
                const changeType = isIntro ? "intro" : isTask ? "task" : null
                const canShowReason = !!changeType && !isTaskOrder

                return (
                    <Frag key={group}>
                        <Bound acceptId={id} group={group} referenceId={referenceId} reviewIds={reviewIds}>
                            <AcceptedCard sx={{ mb: 3, pb: 0 }} id={id} data-cy={`difference-card-${group}`}>
                                <CardHeader
                                    className="difference-card-header"
                                    sx={{ pb: 0, wordBreak: "break-word" }}
                                    title={
                                        <>
                                            <Typography sx={{ mb: 1 }} variant="subtitle1">
                                                {group}
                                            </Typography>

                                            {header}
                                            <Divider sx={{ mx: -3 }} />
                                        </>
                                    }
                                    disableTypography
                                />

                                <Box sx={{ px: 2, overflow: "hidden" }}>
                                    <ComparisonTypeContentWrapper.Wrapper
                                        id={id}
                                        to={to}
                                        from={from}
                                        rendered={list}
                                        components={toRender}
                                        canShowReason={canShowReason}
                                        changeType={changeType}
                                    >
                                        {toRender.map((component, index, ar) => {
                                            const { type: Type, props, key } = component
                                            return (
                                                <Frag key={key ?? index}>
                                                    <DifferenceEntry.Slot>
                                                        <Type {...props} />
                                                    </DifferenceEntry.Slot>
                                                    {index !== ar.length - 1 && (
                                                        <Divider sx={{ mx: -3 }} showEdit={false} />
                                                    )}
                                                </Frag>
                                            )
                                        })}
                                        <Stack spacing={2} mt={2}>
                                            {canShowReason && (
                                                <Box mb={2} mx={3}>
                                                    <ReasonComponentComparison item={to} changeType={changeType} />
                                                </Box>
                                            )}
                                            <InlineCompareButtons
                                                idFromGroup={id}
                                                to={to}
                                                from={from}
                                                rendered={list}
                                            />
                                        </Stack>
                                    </ComparisonTypeContentWrapper.Wrapper>
                                </Box>
                            </AcceptedCard>
                        </Bound>
                    </Frag>
                )
            })}
        </Bound>
    )
}

function AcceptedCard({ id, ...props }) {
    const { target, acceptId, reviewIds = [] } = useBoundContext()
    const resolvedId = id ?? acceptId ?? target.id
    const trackedReviewIds = reviewIds.length ? reviewIds : [resolvedId]
    const isAccepted = trackedReviewIds.every((reviewId) => IsAccepted.call({ isAccepted: false, id: reviewId }))
    AcceptedChanged.after.useRefresh(noChange)

    if (!isAccepted) {
        return <Card className="not-accepted" {...props} />
    }
    return <Card className="accepted" {...props} sx={{ ...props.sx }} />
}

function getReviewIdsForGroup({ id, list = [] }) {
    if (id !== "intro") return []

    const reviewIds = list
        .map((item) => getReviewIdFromAttributes(getAttributesFor(item) ?? getAttributesFor(item?.props?.children)))
        .filter(Boolean)

    return Array.from(new Set(reviewIds))
}

function getReviewIdFromAttributes(attributes = {}) {
    switch (attributes?.name) {
        case "intro.content":
            return "intro.content"
        case "intro.note":
            return "intro.note"
        default:
            return null
    }
}

function getCompareTargets(list) {
    const firstChildProps = list[0]?.props?.children?.props
    const attributes = list
        .map((item) => getAttributesFor(item) ?? getAttributesFor(item?.props?.children))
        .find(Boolean)

    const to = removeEmpty(firstChildProps?.compare, attributes?.compare, firstChildProps?.to, attributes?.to)
    let from = removeEmpty(firstChildProps?.comparedWith, firstChildProps?.from, attributes?.from)
    if (attributes?.to && !Object.keys(attributes.to).length && attributes?.compare) {
        from = to.isHeader ? defaultNewHeader() : defaultNewTask()
    }
    return { to, from }
}

function removeEmpty(...values) {
    for (const value of values) {
        if (value === undefined || value === null) continue
        if (Array.isArray(value) && value.length === 0) continue
        if (typeof value === "object" && Object.keys(value).length === 0) continue
        return value
    }
    return undefined
}
